import { useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import moment from "moment";
import { DataTable } from "../../../CommonComponents/DataTable/DataTable";
import ReusableButton from "../../../CommonComponents/Button/Button";
import ReactLoading from "react-loading";
import { API } from "../../../Networking/API";
import { OngoingDriveSelect } from "./OngoingDriveSelect";
import { candidateBelongsToDrive } from "./useOngoingDriveFilter";

const INTERVIEW_STATUS_CONFIG = {
  not_started: { label: "Not Started", color: "#616161", bg: "#f5f5f5" },
  pending: { label: "Pending", color: "#ef6c00", bg: "#fff3e0" },
  in_progress: { label: "In Progress", color: "#1565c0", bg: "#e3f2fd" },
  completed: { label: "Completed", color: "#2e7d32", bg: "#e8f5e9" },
  terminated: { label: "Terminated", color: "#c62828", bg: "#ffebee" },
  abandoned: { label: "Abandoned", color: "#ef6c00", bg: "#fff3e0" },
};

const datagridSx = {
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "rgb(0, 114, 142) !important",
  },
  "& .MuiDataGrid-iconSeparator": {
    display: "none",
  },
  "& .MuiDataGrid-columnHeaderTitleContainer": {
    color: "#ffffff !important",
    fontSize: "0.9rem !important",
  },
};

function StatusChip({ label, color, bg }) {
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        backgroundColor: bg,
        color,
        fontWeight: 600,
        fontSize: "0.75rem",
      }}
    />
  );
}

function formatInterviewDate(value) {
  if (!value) return "—";
  return moment(value).format("DD MMM YYYY");
}

export function AdminAiInterviewHiredTab({ setNotify, driveFilter }) {
  const {
    drives,
    selectedDriveId,
    setSelectedDriveId,
    selectedDrive,
    driveEmails,
    loadingDrives,
    loadingCandidates,
    hasDrives,
    formatDriveStatus,
  } = driveFilter;

  const [allRows, setAllRows] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [sendingEmailId, setSendingEmailId] = useState(null);
  const [sendingBulk, setSendingBulk] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const fetchHiredCandidates = useCallback(async () => {
    setLoading(true);
    const result = await API.getHiredAiInterviewCandidates();

    if (result?.error) {
      setAllRows([]);
      setNotify({
        isOpen: true,
        message:
          result.error?.response?.data?.message ||
          "Failed to load hired candidates. Check that the backend is running on port 5001.",
        type: "error",
      });
      setLoading(false);
      return;
    }

    const body = result?.response?.data;
    const data = Array.isArray(body?.data) ? body.data : [];

    setAllRows(data);
    setLoading(false);
  }, [setNotify]);

  useEffect(() => {
    fetchHiredCandidates();
  }, [fetchHiredCandidates]);

  const filteredRows = useMemo(() => {
    if (!hasDrives || !driveEmails.size) return [];

    let list = allRows.filter((row) => candidateBelongsToDrive(row, driveEmails));

    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (row) =>
          row.candidateName?.toLowerCase().includes(q) ||
          row.email?.toLowerCase().includes(q)
      );
    }

    return list.map((item, index) => ({
      ...item,
      sno: index + 1,
    }));
  }, [allRows, driveEmails, hasDrives, search]);

  useEffect(() => {
    setSelectedIds([]);
  }, [selectedDriveId, search]);

  const tableLoading =
    loading || loadingDrives || loadingCandidates;

  const handleSendEmail = async (candidateId) => {
    setSendingEmailId(candidateId);
    const result = await API.sendAiInterviewEmail(candidateId);

    if (result?.response?.status === true) {
      setNotify({
        isOpen: true,
        message: result.response.message,
        type: "success",
      });
    } else {
      setNotify({
        isOpen: true,
        message:
          result?.response?.message ||
          result?.error?.response?.data?.message ||
          "Failed to send email. Please try again.",
        type: "error",
      });
    }
    setSendingEmailId(null);
  };

  const handleSendBulkEmail = async () => {
    if (selectedIds.length === 0) return;

    setSendingBulk(true);
    const result = await API.sendBulkAiInterviewEmail(selectedIds);

    if (result?.response?.status === true) {
      setNotify({
        isOpen: true,
        message: result.response.message,
        type: "success",
      });
      setSelectedIds([]);
    } else {
      setNotify({
        isOpen: true,
        message:
          result?.response?.message ||
          result?.error?.response?.data?.message ||
          "Failed to send bulk emails. Please try again.",
        type: "error",
      });
    }
    setSendingBulk(false);
  };

  const columns = useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.No",
        width: 70,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "candidateName",
        headerName: "Candidate Name",
        flex: 1,
        minWidth: 140,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "email",
        headerName: "Email",
        flex: 1.2,
        minWidth: 180,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "jobRole",
        headerName: "Department",
        flex: 1,
        minWidth: 120,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "driveName",
        headerName: "Drive",
        flex: 1,
        minWidth: 110,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "status",
        headerName: "CrackIT Status",
        width: 115,
        headerClassName: "super-app-theme--header",
        renderCell: () => (
          <StatusChip label="Hired" color="#2e7d32" bg="#e8f5e9" />
        ),
      },
      {
        field: "interviewStatus",
        headerName: "AI Interview",
        width: 125,
        headerClassName: "super-app-theme--header",
        renderCell: (params) => {
          const key = params.row.interviewStatus?.toLowerCase();
          const config =
            INTERVIEW_STATUS_CONFIG[key] || INTERVIEW_STATUS_CONFIG.not_started;
          return (
            <StatusChip
              label={config.label}
              color={config.color}
              bg={config.bg}
            />
          );
        },
      },
      {
        field: "score",
        headerName: "Score",
        width: 80,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "interviewDate",
        headerName: "Interview Date",
        width: 120,
        headerClassName: "super-app-theme--header",
        valueFormatter: (params) => formatInterviewDate(params.value),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 140,
        sortable: false,
        headerClassName: "super-app-theme--header",
        renderCell: (params) => (
          <ReusableButton
            size="small"
            text={sendingEmailId === params.row.id ? "Sending..." : "Send Email"}
            startIcon={<EmailOutlinedIcon />}
            disabled={sendingEmailId === params.row.id || sendingBulk}
            onClick={() => handleSendEmail(params.row.id)}
            style={{
              backgroundColor: "#00728E",
              textTransform: "capitalize",
              fontSize: "0.75rem",
              padding: "4px 10px",
            }}
          />
        ),
      },
    ],
    [sendingEmailId, sendingBulk]
  );

  return (
    <>
      <Box className="ai-admin-toolbar" sx={{ mx: "20px", mb: 1 }}>
        <Box className="ai-admin-toolbar-left">
          <TextField
            size="small"
            placeholder="Search by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 260, backgroundColor: "#fff" }}
          />
          <OngoingDriveSelect
            drives={drives}
            selectedDriveId={selectedDriveId}
            onChange={setSelectedDriveId}
            loading={loadingDrives || loadingCandidates}
            formatDriveStatus={formatDriveStatus}
          />
        </Box>
      </Box>

      {selectedDrive && !tableLoading && (
        <Box sx={{ mx: "20px", mb: 1, fontSize: "0.8rem", color: "#757575" }}>
          Showing hired candidates in{" "}
          <strong>
            {selectedDrive.name}
            {selectedDrive.runningStatus
              ? ` (${formatDriveStatus(selectedDrive.runningStatus)})`
              : ""}
          </strong>
          {filteredRows.length > 0 &&
            ` (${filteredRows.length} candidate${filteredRows.length === 1 ? "" : "s"})`}
        </Box>
      )}

      {!tableLoading && filteredRows.length > 0 && (
        <Box className="ai-interview-toolbar">
          <span className="ai-interview-selection-count">
            {selectedIds.length > 0
              ? `${selectedIds.length} candidate${selectedIds.length === 1 ? "" : "s"} selected`
              : "Select candidates to send bulk email"}
          </span>
          <ReusableButton
            text={
              sendingBulk
                ? "Sending..."
                : `Send Email${selectedIds.length > 1 ? "s" : ""}${selectedIds.length > 0 ? ` (${selectedIds.length})` : ""}`
            }
            startIcon={<EmailOutlinedIcon />}
            disabled={
              selectedIds.length === 0 || sendingBulk || !!sendingEmailId
            }
            onClick={handleSendBulkEmail}
            style={{
              backgroundColor: selectedIds.length > 0 ? "#00728E" : "#bdbdbd",
              textTransform: "capitalize",
              fontSize: "0.85rem",
              padding: "6px 16px",
            }}
          />
        </Box>
      )}

      <Box className="ai-interview-table-wrapper tables">
        {tableLoading ? (
          <Box className="ai-interview-loading">
            <ReactLoading
              type="spinningBubbles"
              color="#00728E"
              height={80}
              width={80}
            />
          </Box>
        ) : !hasDrives ? (
          <Box className="ai-interview-empty">No drives found</Box>
        ) : filteredRows.length === 0 ? (
          <Box className="ai-interview-empty">
            No hired candidates found for this drive. Mark candidates as HIRED in
            the selected drive to see them here.
          </Box>
        ) : (
          <DataTable
            className="styleTable"
            rows={filteredRows}
            columns={columns}
            sx={datagridSx}
            autoHeight
            rowHeight={56}
            pageSize={10}
            rowsPerPageOptions={[10]}
            checkboxSelection
            selectionModel={selectedIds}
            onSelectionModelChange={(newSelection) =>
              setSelectedIds(newSelection)
            }
            disableSelectionOnClick={false}
            hideFooterSelectedRowCount={false}
          />
        )}
      </Box>
    </>
  );
}

export default AdminAiInterviewHiredTab;
